import {
  findNode,
  getAboveNode,
  getNode,
  insertNodes,
  isExpanded,
  setNodes,
  TElement,
  TReactEditor,
  wrapNodes,
} from '@udecode/plate';
import { MutableRefObject } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { Editor, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { ELEMENT_BOARD } from '../../board';
import { ELEMENT_COL, ELEMENT_MULTI_COL } from '../../multi-col';
import { ELEMENT_TABLE_PLUS } from '../../table-plus';
import { allSides, isSideDropEnabled } from '../components/Draggable';
import { DragItemBlock, DropDirection } from '../types';
import { getHoverDirection } from '../utils/getHoverDirection';
import { getNewDirection } from '../utils/getNewDirection';

export const useDropBlockOnEditor = (
  editor: TReactEditor,
  {
    blockRef,
    id,
    dropLine,
    setDropLine,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blockRef: MutableRefObject<any>;
    id: string;
    dropLine: DropDirection;
    setDropLine: (dir: DropDirection) => void;
  }
) => {
  return useDrop({
    accept: 'block',
    drop: (dragItem: DragItemBlock, monitor: DropTargetMonitor) => {
      Editor.withoutNormalizing(editor, () => {
        const direction = getHoverDirection(dragItem, monitor, blockRef, id);
        if (!direction) return;

        const dragEntry = findNode(editor, {
          at: [],
          match: { id: dragItem.id },
        });
        if (!dragEntry || !id) return;
        const [, dragPath] = dragEntry;

        ReactEditor.focus(editor);

        let dropPath: Path | undefined;
        if (direction === 'bottom') {
          dropPath = findNode(editor, { at: [], match: { id } })?.[1];
          if (!dropPath) return;

          if (Path.equals(dragPath, Path.next(dropPath))) return;
        }

        if (direction === 'top') {
          const nodePath = findNode(editor, { at: [], match: { id } })?.[1];

          if (!nodePath) return;
          dropPath = [...nodePath.slice(0, -1), nodePath[nodePath.length - 1] - 1];

          if (Path.equals(dragPath, dropPath)) return;
        }

        // TODO: Add comments
        if (['left', 'right'].includes(direction) && isSideDropEnabled) {
          const dragNode = getNode<{ type: string; text: string }>(editor, dragPath);
          if (dragNode?.type && [ELEMENT_TABLE_PLUS, ELEMENT_BOARD].includes(dragNode.type)) return;
          const dropNodeEntry = findNode(editor, { at: [], match: { id } });
          if (dropNodeEntry) {
            const [, dropNodePath] = dropNodeEntry;
            dropPath = dropNodePath;
            if (!dropPath) return;
            const colNodeEntry = getAboveNode(editor, { at: dropPath });
            if (!colNodeEntry) return;
            const [colNode] = colNodeEntry;
            const insertNodePath =
              direction === 'left'
                ? [dropPath[0], dropPath[1] || 0]
                : [dropPath[0], (dropPath[1] || 0) + 1];
            const moveToPath =
              direction === 'left'
                ? [dropPath[0], dropPath[1] || 0, 0]
                : [dropPath[0], (dropPath[1] || 0) + 1, 0];

            // Drop to the existing column
            if (colNode.type === ELEMENT_COL) {
              insertNodes(editor, { type: ELEMENT_COL, children: [] }, { at: insertNodePath });
              Transforms.moveNodes(editor, {
                at: dragPath,
                to: moveToPath,
              });
            } else if (colNodeEntry) {
              const [, abovePath] = colNodeEntry;
              // create new multi column layout
              if (abovePath.length === 0) {
                wrapNodes(editor, { type: ELEMENT_COL, children: [] }, { at: dropPath });
                wrapNodes(editor, { type: ELEMENT_MULTI_COL, children: [] }, { at: dropPath });
                insertNodes(editor, { type: ELEMENT_COL, children: [] }, { at: insertNodePath });
                Transforms.moveNodes(editor, {
                  at: dragPath,
                  to: moveToPath,
                });
              }
            }

            // Reset the column widths
            const multiColNode = editor.children?.[dropPath[0]] as TElement;
            if (multiColNode?.type === ELEMENT_MULTI_COL && dropPath?.[0] !== undefined) {
              const avgWidth = 100 / multiColNode.children.length;
              multiColNode.children.map((child, index) => {
                setNodes(editor, { width: `${avgWidth}%` }, { at: [dropPath?.[0] || 0, index] });
              });
            }

            return;
          }
        }

        if (allSides.includes(direction)) {
          const _dropPath = dropPath as Path;
          const before = Path.isBefore(dragPath, _dropPath) && Path.isSibling(dragPath, _dropPath);
          const to = before ? _dropPath : Path.next(_dropPath);
          Transforms.moveNodes(editor, {
            at: dragPath,
            to,
          });
        }
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    hover(item: DragItemBlock, monitor: DropTargetMonitor) {
      const direction = getHoverDirection(item, monitor, blockRef, id);
      const dropLineDir = getNewDirection(dropLine, direction);
      if (dropLineDir) setDropLine(dropLineDir);

      if (direction && isExpanded(editor.selection)) {
        ReactEditor.focus(editor);
        Transforms.collapse(editor);
      }
    },
  });
};
